import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Shield, UserCircle2, HelpCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { OnboardingTour } from "@/components/OnboardingTour";
import { CongoBorderTop } from "@/components/decorative/CongoPattern";

export const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    checkAdmin();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAdmin();
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      setIsAdmin(!!roles);
    } else {
      setIsAdmin(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-background border-b border-border shadow-md">
      <div className="container flex h-16 items-center justify-between">

        <Link to="/" className="flex items-center group">
          <span className="font-righteous text-3xl md:text-4xl text-foreground hover:text-primary hover:scale-105 transition-all duration-300">
            BrazzaBoost
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-4">
          <Link
            to="/opportunities"
            className="relative text-sm font-medium font-ubuntu text-foreground hover:text-primary transition-colors after:content-[''] after:absolute after:w-full after:h-0.5 after:bg-gradient-to-r after:from-[hsl(var(--congo-green))] after:via-[hsl(var(--congo-yellow))] after:to-[hsl(var(--congo-red))] after:bottom-0 after:left-0 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-200 after:origin-left"
          >
            Opportunités
          </Link>
          <Link
            to="/blog"
            className="relative text-sm font-medium font-ubuntu text-foreground hover:text-primary transition-colors after:content-[''] after:absolute after:w-full after:h-0.5 after:bg-gradient-to-r after:from-[hsl(var(--congo-green))] after:via-[hsl(var(--congo-yellow))] after:to-[hsl(var(--congo-red))] after:bottom-0 after:left-0 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-200 after:origin-left"
          >
            Blog
          </Link>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/cv-builder")}
            className="flex items-center gap-2 border-primary/20 hover:border-primary hover:bg-primary/10"
          >
            <Sparkles className="h-4 w-4 text-primary" />
            <span>Créer mon CV</span>
          </Button>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setShowTour(true)}
                  className="hover:bg-primary/10"
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Guide de démarrage</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate("/auth")}
                  className="opacity-40 hover:opacity-100 hover:bg-primary/5 transition-all"
                >
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Administration</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button onClick={() => navigate("/publish")}>
            Publier une offre
          </Button>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t">
          <nav className="container py-4 space-y-3">
            <Link
              to="/opportunities"
              className="block text-sm font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              Opportunités
            </Link>
            <Link
              to="/blog"
              className="block text-sm font-medium transition-colors hover:text-primary"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </Link>
            
            <Button 
              variant="outline"
              className="w-full justify-start border-primary/20" 
              onClick={() => {
                navigate("/cv-builder");
                setIsMenuOpen(false);
              }}
            >
              <Sparkles className="h-4 w-4 mr-2 text-primary" />
              Créer mon CV
            </Button>
            
            <Button 
              variant="outline"
              className="w-full justify-start"
              onClick={() => {
                setShowTour(true);
                setIsMenuOpen(false);
              }}
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Guide de démarrage
            </Button>
            
            <Button 
              variant="ghost"
              className="w-full justify-start opacity-40 hover:opacity-100 hover:bg-primary/5 transition-all" 
              onClick={() => {
                navigate("/auth");
                setIsMenuOpen(false);
              }}
            >
              <Shield className="h-4 w-4 mr-2 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Administration</span>
            </Button>
            
            <Button 
              className="w-full" 
              onClick={() => {
                navigate("/publish");
                setIsMenuOpen(false);
              }}
            >
              Publier une offre
            </Button>
          </nav>
        </div>
      )}
      
      <OnboardingTour forceOpen={showTour} onClose={() => setShowTour(false)} />
    </header>
  );
};
